import type { HsaSavingsValues } from './config';
import type { HsaSavingsResult } from './calculate';
import { formatDollarPrecise } from './BarChart';
import { balanceMix } from './modules';

// Donut geometry (SVG user units).
const SIZE = 120;
const RADIUS = 48;
const STROKE = 14;
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

/**
 * "Ending balance mix" module (Brad, 2026-10-01; the card from the lead
 * designer's mockup). Wording is the mockup's: this module has no prod
 * equivalent, so it's opt-in per placement and off everywhere by default.
 * The donut is SVG (not the mockup's canvas) so it scales crisply and
 * needs no redraw code; the same figures are listed as text below it.
 */
export function BalanceMixCard({ values, result }: { values: HsaSavingsValues; result: HsaSavingsResult }) {
  const mix = balanceMix(values, result);
  const total = mix.netDeposits + mix.earnings;
  const depositsLen = total > 0 ? (mix.netDeposits / total) * CIRCUMFERENCE : 0;
  const earningsLen = total > 0 ? (mix.earnings / total) * CIRCUMFERENCE : 0;
  const percent = Math.round(mix.earningsShare * 100);

  // Deposits is drawn as a plain, full, untrimmed circle -- not a
  // proportional arc. Earnings is drawn on top of it as its own arc, so
  // earnings' round caps always land on (and visibly overlap) the deposits
  // ring underneath at *both* ends, with no gap possible anywhere (Brad,
  // 2026-10-02: "the circle's full background is the dark green and the
  // light green has to overlap [it]" -- not a trimmed arc with a gap on one
  // side, which the previous two passes both still had). Deposits' own
  // visible share comes out right regardless, since it's just whatever
  // earnings doesn't paint over.
  const depositsVisible = mix.netDeposits > 0;

  return (
    <div className="hsab-calc-savings__odon-card hsab-calc-savings__odon-mix">
      <h3 className="hsab-calc-savings__odon-card-title">Ending balance mix</h3>
      <p className="hsab-calc-savings__odon-card-subtitle">Net deposits compared with estimated investment earnings.</p>

      <div className="hsab-calc-savings__odon-donut">
        <svg
          viewBox={`0 0 ${SIZE} ${SIZE}`}
          role="img"
          aria-label="Donut chart showing net deposits and investment earnings"
        >
          <circle
            className="hsab-calc-savings__odon-donut-track"
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            strokeWidth={STROKE}
          />
          {depositsVisible && (
            <circle
              className="hsab-calc-savings__odon-donut-deposits"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
            />
          )}
          {earningsLen > 0 && (
            <circle
              className="hsab-calc-savings__odon-donut-earnings"
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              strokeWidth={STROKE}
              strokeDasharray={`${earningsLen} ${CIRCUMFERENCE}`}
              strokeDashoffset={-depositsLen}
              transform={`rotate(-90 ${SIZE / 2} ${SIZE / 2})`}
            />
          )}
        </svg>
        <div className="hsab-calc-savings__odon-donut-center">
          <strong>{percent}%</strong>
          <span>from earnings</span>
        </div>
      </div>

      <dl className="hsab-calc-savings__odon-breakdown">
        <div className="hsab-calc-savings__odon-breakdown-row">
          <dt>
            <i className="hsab-calc-savings__odon-swatch hsab-calc-savings__odon-swatch--deposits" aria-hidden="true" />
            Net deposits
          </dt>
          <dd>{formatDollarPrecise(mix.netDeposits)}</dd>
        </div>
        <div className="hsab-calc-savings__odon-breakdown-row">
          <dt>
            <i className="hsab-calc-savings__odon-swatch hsab-calc-savings__odon-swatch--earnings" aria-hidden="true" />
            Investment earnings
          </dt>
          <dd>{formatDollarPrecise(mix.earnings)}</dd>
        </div>
        <div className="hsab-calc-savings__odon-breakdown-row">
          <dt>Medical expenses</dt>
          <dd>{formatDollarPrecise(mix.medicalExpenses)}</dd>
        </div>
      </dl>
    </div>
  );
}
